import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { api, throwIfError } from "../client";
import { nvpKeys } from "../query-keys";
import type { CountryCode } from "@/types/identifiers";

export { nvpKeys } from "../query-keys";

export type BoatStatus = "online" | "offline" | "unknown";

export function useNvpStatus() {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: nvpKeys.status,
    queryFn: async () => {
      const result = await api.GET("/api/nvp/status");
      return throwIfError(result, "Failed to fetch NVP status");
    },
    refetchInterval: 30_000,
    staleTime: 10_000,
  });

  const boats = query.data?.boats;

  const getNvpStatus = useCallback(
    (boatCode: CountryCode | string | null | undefined): BoatStatus => {
      if (!boatCode || !boats) return "unknown";
      const status = boats[boatCode as keyof typeof boats];
      if (status === true) return "online";
      if (status === false) return "offline";
      return "unknown";
    },
    [boats],
  );

  const isBoatOnline = useCallback(
    (boatCode: CountryCode | string | null | undefined): boolean =>
      getNvpStatus(boatCode) === "online",
    [getNvpStatus],
  );

  const refresh = useCallback(() => {
    qc.invalidateQueries({ queryKey: nvpKeys.status });
  }, [qc]);

  return { ...query, getNvpStatus, isBoatOnline, refresh };
}
